import './App.css';
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { lazy, Suspense, useEffect } from "react";

// Providers
import ShopContextProvider from './context/ShopContext';
import AuthProvider        from './context/AuthContext';
import ThemeProvider       from './context/ThemeContext';

// Public pages
import Shop            from './pages/Shop';

// Admin

// Dashboard

// Layout
import NewNav  from './components/NewNav';
import MobileTabBar from './components/MobileTabBar';
import Footer  from './components/Footer';

// Pages load on demand (code-splitting): only the home page is in the main bundle, so visitors
// do not download the admin panel, dashboard, checkout etc. before they need them.
const ShopCategory = lazy(() => import('./pages/ShopCategory'));
const LoginSignup = lazy(() => import('./pages/LoginSignup'));
const Product = lazy(() => import('./pages/Product'));
const Cart = lazy(() => import('./pages/Cart'));
const AllListings = lazy(() => import('./pages/AllListings'));
const SellerProfile = lazy(() => import('./pages/SellerProfile'));
const Checkout = lazy(() => import('./pages/Checkout'));
const Companies = lazy(() => import('./pages/Companies'));
const Contact = lazy(() => import('./pages/Contact'));
const About = lazy(() => import('./pages/Info/About'));
const HowItWorks = lazy(() => import('./pages/Info/HowItWorks'));
const TrovePay = lazy(() => import('./pages/Info/TrovePay'));
const SafetyTips = lazy(() => import('./pages/Info/SafetyTips'));
const FAQ = lazy(() => import('./pages/Info/FAQ'));
const Terms = lazy(() => import('./pages/Info/Terms'));
const Privacy = lazy(() => import('./pages/Info/Privacy'));
const Cookies = lazy(() => import('./pages/Info/Cookies'));
const AdminPanel = lazy(() => import('./pages/AdminPanel'));
const DashboardLayout = lazy(() => import('./pages/Dashboard/DashboardLayout'));
const Overview = lazy(() => import('./pages/Dashboard/Overview'));
const MyItems = lazy(() => import('./pages/Dashboard/MyItems'));
const PostAd = lazy(() => import('./pages/Dashboard/PostAd'));
const Messages = lazy(() => import('./pages/Dashboard/Messages'));
const Offers = lazy(() => import('./pages/Dashboard/Offers'));
const Favorites = lazy(() => import('./pages/Dashboard/Favorites'));
const SavedSearches = lazy(() => import('./pages/Dashboard/SavedSearches'));
const Ratings = lazy(() => import('./pages/Dashboard/Ratings'));
const Profile = lazy(() => import('./pages/Dashboard/Profile'));
const BusinessProfile = lazy(() => import('./pages/Dashboard/BusinessProfile'));
const Verification = lazy(() => import('./pages/Dashboard/Verification'));
const Referrals = lazy(() => import('./pages/Dashboard/Referrals'));
const Analytics = lazy(() => import('./pages/Dashboard/Analytics'));
const ImportListings = lazy(() => import('./pages/Dashboard/ImportListings'));
const Escrow = lazy(() => import('./pages/Dashboard/Escrow'));
const EscrowOrder = lazy(() => import('./pages/Dashboard/EscrowOrder'));
const Finance = lazy(() => import('./pages/Dashboard/Finance'));
const Promotions = lazy(() => import('./pages/Dashboard/ComingSoon').then((m) => ({ default: m.Promotions })));

// Categories list (keep exactly as before for ShopCategory routing)
const categories = [
  "Electronics","Property","Vehicles","Home & Furniture","Fashion & Beauty",
  "Hobbies & Entertainment","Services","Garden & Outdoor","Jobs",
  "Agriculture & Food","Gadgets & Accessories","Baby & Kids","Misc & Others","Adult"
];

// Shown for a moment while a page's code downloads (the nav and footer stay on screen)
function RouteFallback() {
  return <div role="status" style={{ minHeight: "55vh", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text-muted)", fontSize: 14 }}>Loading…</div>;
}

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => { if (!hash) window.scrollTo({ top: 0, behavior: "smooth" }); }, [pathname, hash]);
  return null;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <ShopContextProvider>
          <BrowserRouter>
            <ScrollToTop />
            <Suspense fallback={<RouteFallback />}>
            <Routes>

              {/* ── Admin: standalone, no site nav ── */}
              {/* /admin/login, /admin/listings, /admin/users ... are all handled inside AdminPanel (it shows the sign-in page itself) */}
              <Route path="/admin/*"         element={<AdminPanel />} />

              {/* ── Main site: nav + footer ── */}
              <Route path="/*" element={
                <div className="site-shell">
                  <NewNav />
                  <main className="site-main">
                    <Suspense fallback={<RouteFallback />}>
                    <Routes>
                      <Route path="/"    element={<Shop />} />

                      {/* Category pages */}
                      {categories.map((cat, i) => (
                        <Route
                          key={i}
                          path={`/category/${cat.toLowerCase().replace(/ & /g, "-").replace(/ /g, "-")}`}
                          element={<ShopCategory category={cat} />}
                        />
                      ))}

                      <Route path="/product/:productId"      element={<Product />} />
                      <Route path="/cart"                    element={<Cart />} />
                      <Route path="/login"                   element={<LoginSignup />} />
                      <Route path="/all"                     element={<AllListings />} />
                      <Route path="/search"                  element={<AllListings />} />
                      <Route path="/seller/:username"        element={<SellerProfile />} />
                      <Route path="/checkout/cart/:sellerId" element={<Checkout />} />
                      <Route path="/checkout/:listingId"     element={<Checkout />} />
                      <Route path="/companies"               element={<Companies />} />
                      <Route path="/contact"                 element={<Contact />} />
                      <Route path="/about"                   element={<About />} />
                      <Route path="/how-it-works"            element={<HowItWorks />} />
                      <Route path="/trovepay"                element={<TrovePay />} />
                      <Route path="/safety-tips"             element={<SafetyTips />} />
                      <Route path="/faq"                     element={<FAQ />} />
                      <Route path="/terms"                   element={<Terms />} />
                      <Route path="/privacy"                 element={<Privacy />} />
                      <Route path="/cookies"                 element={<Cookies />} />

                      {/* Dashboard */}
                      <Route path="/dashboard" element={<DashboardLayout />}>
                        <Route index                     element={<Overview />} />
                        <Route path="items"              element={<MyItems />} />
                        <Route path="post"               element={<PostAd />} />
                        <Route path="post/:id"           element={<PostAd />} />
                        <Route path="messages"           element={<Messages />} />
                        <Route path="messages/:threadId" element={<Messages />} />
                        <Route path="offers"             element={<Offers />} />
                        <Route path="favorites"          element={<Favorites />} />
                        <Route path="alerts"             element={<SavedSearches />} />
                        <Route path="ratings"            element={<Ratings />} />
                        <Route path="profile"            element={<Profile />} />
                        <Route path="business"           element={<BusinessProfile />} />
                        <Route path="verification"       element={<Verification />} />
                        <Route path="referrals"          element={<Referrals />} />
                        <Route path="promotions"         element={<Promotions />} />
                        <Route path="analytics"          element={<Analytics />} />
                        <Route path="import"             element={<ImportListings />} />
                        <Route path="escrow"             element={<Escrow />} />
                        <Route path="escrow/:id"         element={<EscrowOrder />} />
                        <Route path="finance"            element={<Finance />} />
                      </Route>
                    </Routes>
                    </Suspense>
                  </main>
                  <Footer />
                  <MobileTabBar />
                </div>
              } />

            </Routes>
            </Suspense>
          </BrowserRouter>
        </ShopContextProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;